/**
 * Recipe Metadata Sync Service
 *
 * Persists user recipe metadata (ratings, verdicts, notes, cooked-stamps,
 * favorites, timesPlanned) to Firestore, and subscribes to it live.
 *
 * Ratings and cooked stamps are shared at the household level. NOT plain
 * last-write-wins any more: a write publishes only the fields its action owns
 * (see the `fields` argument), because the alternative — republishing the whole
 * local snapshot — is how a stale tab overwrote the partner's fresh rating.
 * Retracting a verdict needs its own deletion path, since a merge write can
 * only add or overwrite a map key, never remove one.
 *
 * The cooked stamp also carries attribution (`lastMadeBy`) so the UI can show
 * "last cooked by X".
 *
 * Path: households/{hid}/recipeMetadata/{recipeId} when paired,
 *       users/{uid}/recipeMetadata/{recipeId}     when solo.
 */

import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteField,
  getDocs,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  type DocumentData,
  type UpdateData,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { resolveDataScope, buildScopedPath } from './householdPaths';
import type { RecipeMetadata } from '../redux/recipeMetadataSlice';

interface FirestoreRecipeMetadata extends RecipeMetadata {
  updatedAt: Timestamp | string;
}

const getRecipeMetadataCollection = (
  userId: string,
  householdId?: string | null
) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(scope, 'recipeMetadata');
  return collection(firestore, root, id, ...rest);
};

const getRecipeMetadataDocRef = (
  userId: string,
  recipeId: string,
  householdId?: string | null
) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(
    scope,
    'recipeMetadata',
    recipeId
  );
  return doc(firestore, root, id, ...rest);
};

/**
 * Strip undefined fields — Firestore rejects them.
 */
const cleanForFirestore = <T extends Record<string, unknown>>(obj: T): T => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) out[key] = value;
  }
  return out as T;
};

/**
 * Write a recipe's metadata to the cloud.
 *
 * `fields` narrows the write to the keys the caller actually changed. Pushing
 * the whole local snapshot is how one device overwrites the other: nothing
 * receives live updates for free, so a long-lived tab holds stale metadata and
 * `recordViewed` — which fires just by opening a recipe — would republish a
 * stale `rating` over the partner's fresh one. Omitting `fields` keeps the old
 * write-everything behaviour for callers that genuinely mean it.
 */
export const syncRecipeMetadataToCloud = async (
  userId: string,
  metadata: RecipeMetadata,
  householdId?: string | null,
  fields?: Array<keyof RecipeMetadata>
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }
  const docRef = getRecipeMetadataDocRef(userId, metadata.recipeId, householdId);

  const source: Partial<RecipeMetadata> = fields
    ? // recipeId always rides along so a first write creates a complete doc.
      fields.reduce<Partial<RecipeMetadata>>(
        (acc, key) => {
          (acc as Record<string, unknown>)[key] = metadata[key];
          return acc;
        },
        { recipeId: metadata.recipeId }
      )
    : metadata;

  const payload = {
    ...source,
    updatedAt: serverTimestamp() as Timestamp,
  };
  await setDoc(
    docRef,
    cleanForFirestore(payload as unknown as Record<string, unknown>),
    { merge: true }
  );
};

/**
 * Remove one person's verdict from the cloud.
 *
 * `setDoc(..., { merge: true })` cannot delete a map key — it only ever adds or
 * overwrites — so un-tapping a chip removed the verdict locally and left it in
 * Firestore, where the next snapshot resurrected it. `deleteField()` on the
 * exact path is the only write that actually removes it, and it touches nothing
 * else in the document.
 *
 * `rating` is passed in already recomputed from the remaining verdicts;
 * undefined means there was nothing left to derive from, in which case the
 * existing rating is deliberately left alone (same rule as setEaterVerdict).
 */
export const clearEaterVerdictInCloud = async (
  userId: string,
  recipeId: string,
  eaterId: string,
  householdId?: string | null,
  rating?: number
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }
  const docRef = getRecipeMetadataDocRef(userId, recipeId, householdId);
  const payload: Record<string, unknown> = {
    [`verdicts.${eaterId}`]: deleteField(),
    updatedAt: serverTimestamp(),
  };
  if (rating !== undefined) payload.rating = rating;
  // Dotted field paths are how Firestore addresses a single map key; UpdateData
  // cannot express them generically, so the cast is at the API boundary only.
  await updateDoc(docRef, payload as UpdateData<DocumentData>);
};

/**
 * Live-subscribe to every recipe metadata doc under the active scope.
 *
 * Mirrors subscribeToMealPlanWeeks. Without this, ratings and verdicts only
 * crossed devices on a full app reload while the meal plan crossed in seconds —
 * so a cooked stamp appeared on the partner's screen with nobody's answer on it.
 */
export const subscribeToRecipeMetadata = (
  userId: string,
  householdId: string | null | undefined,
  onUpdate: (metadata: Record<string, RecipeMetadata>) => void,
  onError?: (error: Error) => void
): (() => void) => {
  if (!isFirebaseEnabled() || !firestore) {
    return () => {};
  }
  const collectionRef = getRecipeMetadataCollection(userId, householdId);
  return onSnapshot(
    collectionRef,
    (snapshot) => {
      const result: Record<string, RecipeMetadata> = {};
      snapshot.docs.forEach((d) => {
        const { updatedAt: _ignored, ...metadata } =
          d.data() as FirestoreRecipeMetadata;
        result[d.id] = metadata as RecipeMetadata;
      });
      onUpdate(result);
    },
    (error) => {
      console.error('Recipe metadata subscription error:', error);
      onError?.(error);
    }
  );
};

/**
 * Fetch every recipe metadata entry under the active scope, keyed by recipeId.
 */
export const fetchAllRecipeMetadataFromCloud = async (
  userId: string,
  householdId?: string | null
): Promise<Record<string, RecipeMetadata>> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }
  const collectionRef = getRecipeMetadataCollection(userId, householdId);
  const snapshot = await getDocs(collectionRef);
  const result: Record<string, RecipeMetadata> = {};
  snapshot.docs.forEach((d) => {
    const { updatedAt: _ignored, ...metadata } =
      d.data() as FirestoreRecipeMetadata;
    result[d.id] = metadata as RecipeMetadata;
  });
  return result;
};
